import { gridAxes, hasBlock } from "@gen/logic-grid/axes.ts";
import type { Puzzle } from "@gen/logic-grid/types.ts";

/**
 * The staircase, shrunk to a control.
 *
 * Showing one block at a time solves the width problem and creates a new one:
 * fifteen blocks in a flat list, and no sense of where you are in the puzzle or
 * what is left. A dropdown of "Cook × Side" strings would answer neither.
 *
 * So the picker is the shape itself. Each square is one block, in the position
 * it occupies in the printed grid, which means picking a block is a spatial act
 * rather than a naming one — and the reader keeps the picture of the whole
 * puzzle that the staircase was giving them for free.
 *
 * A filled square is a block with marks in it. Copying a solved-in-pencil grid
 * off paper is fifteen screens of work, and this is the only thing on the page
 * that can say how far through that you are.
 */
export default function BlockMap({
  puzzle,
  current,
  marked,
  onPick,
}: {
  puzzle: Puzzle;
  /** The block on screen, lower category first. */
  current: [number, number];
  /** Which blocks have marks in them, keyed `i:j` with i < j. */
  marked: Set<string>;
  onPick: (block: [number, number]) => void;
}) {
  const K = puzzle.categories.length;
  const { rowCats, colCats } = gridAxes(K);

  return (
    <div
      className="inline-grid gap-[2px] shrink-0"
      style={{ gridTemplateColumns: `repeat(${colCats.length}, 1.4rem)` }}
      role="group"
      aria-label="Which block to show"
    >
      {rowCats.flatMap((rc) =>
        colCats.map((cc) => {
          if (!hasBlock(rc, cc)) {
            return <span key={`${rc}-${cc}`} aria-hidden className="w-[1.4rem] h-[1.4rem]" />;
          }
          const block: [number, number] = rc < cc ? [rc, cc] : [cc, rc];
          const isCurrent = block[0] === current[0] && block[1] === current[1];
          const hasMarks = marked.has(`${block[0]}:${block[1]}`);
          const look = isCurrent
            ? "bg-rust border-rust"
            : hasMarks
              ? "bg-faint border-muted"
              : "bg-panel border-line";
          return (
            <button
              key={`${rc}-${cc}`}
              type="button"
              aria-current={isCurrent}
              aria-label={`${puzzle.categories[block[0]]?.name} by ${puzzle.categories[block[1]]?.name}${hasMarks ? ", has marks" : ""}`}
              onClick={() => onPick(block)}
              className={`w-[1.4rem] h-[1.4rem] border rounded-[2px] cursor-pointer p-0 hover:border-rust focus-visible:outline-2 focus-visible:outline-rust ${look}`}
            />
          );
        }),
      )}
    </div>
  );
}
